import UnhappyIcon from '../assets/images/unhappy.png';
const { Card, Overlay, Modal, Button }  = require('react-bootstrap');
const { IsMobile } = require('../utils/Mobile');
const { forwardRef, useContext } = require('react');
const { GameStateContext } = require('../context/GameStateContext');
const { ScorePreview } = require('@tta/engine/Utilities/ScorePreview');
const { GetCardColor, WhiteOutline } = require('./Utilities/Utilities')
const {CivilCardTypes, MilitaryCardTypes, TechnologyTypes} = require('@tta/engine/Constants/Constants')
const { useImages } = require('../context/ImageContext');
const { default: Amount, IconText } = require('./Amount');
const { CardBenefit } = require('@tta/engine/Utilities/CardBenefits');
const TerritoryEffects = require('@tta/engine/Constants/Territories/TerritoryEffects');
const { ShownName } = require('@tta/engine/Utilities/ShownNames');

// The card's top and bottom: its color, except Defense/Colonization cards (grey, their body in their color)
const FrameColor = (card, color) => card.type === MilitaryCardTypes.Bonus ? '#cbc1cb' : card.type === MilitaryCardTypes.Tactic ? '#fbdcdc' : color;
// Cards whose body is in their color with white text, as printed (their top and bottom pale: see FrameColor)
const ColoredBody = [MilitaryCardTypes.Bonus, MilitaryCardTypes.Tactic];

const CardTooltip = forwardRef(({arrowProps, hasDoneInitialMeasure, ...rest}, ref) => {
    return <Card ref={ref}{...rest} />
});

const headerStyle = {
    padding: '2px',
    marginTop: '2px',
    marginBottom: '2px',
    fontWeight:'bold',
    textAlign:'center',
    fontSize: '16px',
    fontFamily: 'Stencil, Times New Roman, Serif',
    textShadow: WhiteOutline,
    verticalAlign: 'middle',
    border: '0px',
}

const footerStyle = {
    height: '25px',
    padding: '2px',
    textAlign:'center',
    // (a white outline, as the age at the top has, so numbers stay readable on the dark colors: e.g. a farm's brown)
    textShadow: WhiteOutline,
    border: '0px',
    marginTop: '2px',
    marginBottom: '2px'
}

const bodyStyle = {
    backgroundColor: '#fdf9dc',
    height: '180px',
    padding: 3,
    display: 'flex',
    flexDirection: 'column',
}

// On a phone (no hover): the card in a sheet over the page instead, closed by OnClose, with what can be done with it
// (Action: { label, onClick }, e.g. taking it from the card row) and a line about it (Note: what the desktop shows on
// hover, e.g. why it can't be taken). Effect: what the card does in this game, under it on a desktop too (e.g. the Nobel
// Prize on the culture board)
const CardOverlay = ({ShowOverlay, ComponentRef, card, callback, OnClose, Action, Note, Effect}) =>
{
    const { images } = useImages();
    const cardColor = GetCardColor(card)

    // (an Age III wonder or event: what it would score now, in its body; the card grows to fit it)
    const game = useContext(GameStateContext);
    const preview = ShowOverlay ? ScorePreview(game?.gameState, card) : null;
    const cardStyle = {
        backgroundColor: FrameColor(card, cardColor),
        padding: 0,
        width: '182px',
        ...(preview || Effect ? { minHeight: '243px' } : { height: '243px' }),
        // (fixed from its first frame, before it is placed: otherwise it sits at the bottom of the page for a moment,
        // making it taller, and a scrollbar coming and going shifts everything under the mouse)
        position: 'fixed',
        boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.5)',
        // Above dialogs (a card hovered in one, e.g. the aggression being played), which sit at 1050-1055
        zIndex: 1090,
    }

    var category = card.category === null ? card.type : card.category;

    if (IsMobile)
    {
        return <CardSheet Show={ShowOverlay} card={card} OnClose={OnClose ?? (() => callback(false))} Action={Action} Note={Note ?? Effect}/>;
    }
    
    // Positioned against the window (fixed) so a popup never makes the page taller, and flipped above the card when
    // there is no room below. Otherwise a popup near the bottom of the page grows the page, the layout shifts out from
    // under the mouse, the popup closes, and it keeps reopening (a stutter).
    return (<Overlay
        show={ShowOverlay}
        target={ComponentRef.current}
        placement="bottom"
        flip
        popperConfig={{ strategy: 'fixed' }}
    >
        <CardTooltip 
        border="0px" 
        style={cardStyle}
        ref = {ComponentRef}
        onMouseEnter = {() => callback(true)}
        onMouseLeave = {() => callback(false)}
        >
            <CardSections card={card} category={category} images={images} preview={preview}/>
            {Effect && <div className="tta-card-effect">{Effect}</div>}
        </CardTooltip>
    </Overlay>)
}

// The phone's card details: the card at a readable size, then the note and what can be done with it; a tap outside it
// (or Close) closes it
export const CardSheet = ({Show, card, OnClose, Action, Note, children}) =>
    <Modal show={Show} onHide={OnClose} centered dialogClassName="tta-card-sheet">
        <Modal.Body>
            {card && <CardFace card={card} scale={1.4}/>}
            {children}
            {Note && <div className="tta-card-sheet-note">{Note}</div>}
            <div className="tta-card-sheet-buttons">
                {Action && <Button variant="primary" onClick={() => { OnClose(); Action.onClick(); }}>{Action.label}</Button>}
                <Button variant="outline-secondary" onClick={OnClose}>Close</Button>
            </div>
        </Modal.Body>
    </Modal>;

// What an Age III wonder or event would score if it happened now (see ScorePreview), compact, in the card's body: a
// number per player in their color (the player looking in bold), then the culture icon; no names (they can be long)
const ScoreNow = ({preview}) => {
    const game = useContext(GameStateContext);
    const { images } = useImages();
    if (!preview)
    {
        return null;
    }
    return (
        <div className="tta-score-now" title={preview.kind === 'wonder' ? 'Culture if completed now' : 'Culture if revealed now'}>
            <span className="tta-muted">Currently:</span>
            {/* (never negative: no signs; the culture icon after each) */}
            {preview.players.map((p, index) =>
                <span key={p.player} className="tta-score-now-entry" style={{ color: game?.colors?.[p.player], fontWeight: p.player === game?.viewer ? 'bold' : 'normal' }}>
                    {index > 0 && <span className="tta-muted"> · </span>}{p.culture}<img src={images.Culture?.src} alt="culture"/></span>)}
        </div>
    );
};

// The kind of card, in the header's right half: long ones smaller, to keep clear of the age in the middle
const CategoryFit = (category = '') => {
    const length = String(category).length;
    return length >= 10 ? { fontSize: '12px', lineHeight: '18px' } : length >= 9 ? { fontSize: '14px', lineHeight: '18px' } : {};
};

// The card's header, body and footer: in the hover overlay, or as a card on its own (CardFace)
const CardSections = ({card, category, images, preview}) => <>
        <Card.Header style = {headerStyle}>
            <div style= {{}}>{card.age.slice(4)}</div>
        </Card.Header>
        {/* Defense/Colonization cards: all in their color, white text, as printed */}
        <Card.Body style={{ ...(ColoredBody.includes(card.type) ? { ...bodyStyle, backgroundColor: GetCardColor(card), color: 'white' } : bodyStyle),
            ...(preview ? { height: 'auto', minHeight: bodyStyle.height } : {}) }}>
            
            {/* (a long name in smaller type, so it stays clear of the age in the middle: e.g. Construction, Government) */}
            <div style = {{textAlign:'right', paddingRight: '2px', fontFamily: 'Arial Narrow, Helvetica, Sans serif', color: 'white', marginTop:'-25px', textShadow: '0px 0px 5px black',
                ...CategoryFit(category)}}>
                {category}
            </div>
            <div style = {{textAlign:'center', fontFamily: 'Times New Roman, Serif', fontWeight:'bold', fontSize:'15px'}}>
                {card.crumbled ? `Crumbled ${ShownName(card.name)}` : card.type === MilitaryCardTypes.Bonus ? card.name.replace('/', ' / ') : ShownName(card.name)}
            </div>
            { GetContents(card, images) }
            {/* (what it would score now, after its text) */}
            <ScoreNow preview={preview}/>
        </Card.Body>
        <Card.Footer style={footerStyle}>
            <CardFooterContent card={card} images={images}/>
        </Card.Footer>
</>;

// A card drawn in full where it stands (not on hover), e.g. events to choose from: exactly as the hover overlay draws it
// (which sits outside the board, so it gets the page's plain text styles, not the board's: see .tta-card-face), shrunk
// to 'scale' of that size
// (dashed: a selected card is outlined dashed, picked but not yet confirmed)
export const CardFace = ({card, onClick, title, selected, scale = 1, dashed = false}) =>
{
    const { images } = useImages();
    const category = card.category === null ? card.type : card.category;
    // (an Age III wonder or event: what it would score now, as on its hover; the card grows to fit it)
    const game = useContext(GameStateContext);
    const preview = ScorePreview(game?.gameState, card);
    return (
        // (scaled with zoom, so the space it takes is its drawn size, whatever its height)
        <div className="tta-card-face" style={{ width: `${182 * scale}px` }} title={title}>
            <Card style={{ backgroundColor: FrameColor(card, GetCardColor(card)), padding: 0, width: '182px', ...(preview ? { minHeight: '243px' } : { height: '243px' }), position: 'relative',
                boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.5)', cursor: onClick ? 'pointer' : 'default', outline: selected ? `3px ${dashed ? 'dashed' : 'solid'} #1a73e8` : 'none', outlineOffset: dashed ? '3px' : 0,
                zoom: scale }}
                onClick={onClick}>
                <CardSections card={card} category={category} images={images} preview={preview}/>
            </Card>
        </div>
    );
}

const GetContents = (card, images) =>
{
    var cardContentStyle = {
        fontFamily: 'Times New Roman, Sans-Serif',
        fontSize: '12px',
        paddingTop: '20px',
        textAlign: 'center'
    }
    var contents = (<div style={cardContentStyle}><IconText text={card.text}/></div>)
    // Leaders: the portrait on the left, the text flowing beside and under it (so a wordy leader keeps a readable size)
    if (card.type === CivilCardTypes.Leader)
    {
        const picture = images?.[card.image];
        // (long texts in smaller type, so they stay on the card: e.g. Hippocrates)
        const length = (card.text ?? '').length;
        const size = length > 230 ? 11 : length > 180 ? 11.5 : length > 150 ? 12 : 13;
        contents = (<div style={{...cardContentStyle, paddingTop: '4px', fontSize: `${size}px`, lineHeight: 1.2, textAlign: 'left', flex: 1}}>
            {picture && <img src={picture.src} alt={card.name} style={{ float: 'left', height: '64px', borderRadius: '4px', margin: '0 6px 2px 0' }}/>}
            <IconText text={card.text}/>
        </div>);
    }
    // Wonders: picture, stage costs and text.
    else if (card.type === CivilCardTypes.Wonder)
    {
        const picture = images?.[card.image];
        // Stage costs sit at the bottom, just above the printed bonuses, as on the physical card
        // (long texts in smaller type, so they stay on the card: e.g. Shakespeare, Bach)
        const length = (card.text ?? '').length;
        const size = length > 230 ? 9 : length > 180 ? 9.5 : length > 150 ? 10 : 11;
        contents = (<div style={{...cardContentStyle, paddingTop: '2px', fontSize: `${size}px`, lineHeight: 1.2, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {picture && <img src={picture.src} alt={card.name} style={{ height: '56px', borderRadius: '4px', marginBottom: '3px' }}/>}
                {/* The leaders who added to this wonder (Homer), by their portrait */}
                {(card.addedHappyFaces ?? []).map((added, index) => images?.[added.image] &&
                    <img key={index} src={images[added.image].src} alt={added.source} title={`${added.source} added a happy face`}
                        style={{ height: '44px', borderRadius: '10px' }}/>)}
            </div>
            {/* (A crumbled wonder's text no longer applies: struck through, still readable) */}
            <div style={card.crumbled ? { textDecoration: 'line-through' } : undefined}><IconText text={card.text}/></div>
            {card.stageCosts && <div className="tta-stage-costs"><Amount value={card.stageCosts.join(' ')} kind={card.stageCurrency ?? "resources"}/></div>}
        </div>);
    }
    if(card.type === CivilCardTypes.Technology)
    {
        // Its science cost (none for the starting cards, never discovered: e.g. Warrior)
        const science = card.Technology.scienceCost > 0 && <div><Amount value={card.Technology.scienceCost} kind="science"/></div>;
        switch (card.Technology.type)
        {
            // A government: its cost (and by revolution), its urban building limit, its civil and military actions
            case TechnologyTypes.Government:
                contents = (<div style={{ ...cardContentStyle, paddingTop: '6px' }} className="tta-card-government">
                    {card.Technology.scienceCost > 0 &&
                        <div className="tta-card-cost"><b>{card.Technology.scienceCost}({card.Technology.revolutionCost})</b><Icon images={images} name="Science"/></div>}
                    <div className="tta-card-icons">{Repeat(card.Technology.urbanBuildingLimit, <Icon images={images} name="Home"/>)}</div>
                    <div className="tta-card-icons">{Repeat(card.Technology.civilActions, <Icon images={images} name="CivilAction"/>)}</div>
                    <div className="tta-card-icons">{Repeat(card.Technology.militaryActions, <Icon images={images} name="MilitaryAction"/>)}</div>
                </div>)
                break;
                // A special technology: its cost and its kind's symbol, then what it does, where it says (construction)
                case TechnologyTypes.Special.Civil:
                case TechnologyTypes.Special.Colonization:
                case TechnologyTypes.Special.Construction:
                case TechnologyTypes.Special.Military:
                    contents = card.Technology.text ? (<div style={{ ...cardContentStyle, paddingTop: '4px' }}>
                        {/* (with a text: the cost and a smaller symbol side by side, to leave it room) */}
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                            {science}
                            <div className="tta-special-tile" style={{ width: '30px', height: '25px' }}>
                                <Icon images={images} name={SpecialSymbols[card.Technology.type]}/>
                            </div>
                        </div>
                        <div style={{ fontSize: '11px', lineHeight: 1.2, marginTop: '4px', padding: '0 6px' }}>
                            {/* (a line of the text each, as printed: e.g. one for each age's discount) */}
                            {card.Technology.text.split('\n').map((line, index) => <div key={index}><IconText text={line}/></div>)}
                        </div>
                    </div>) : (<div style={cardContentStyle}>
                        {science}
                        <div className="tta-special-tile" style={{ margin: '14px auto 0', width: '48px', height: '40px' }}>
                            <Icon images={images} name={SpecialSymbols[card.Technology.type]}/>
                        </div>
                    </div>)
                    break;
                // A card built on: its science cost and, below, its build cost (each large, in its color); a military
                // unit, its unit's symbol under them
                default:
                    contents = (<div style={{ ...cardContentStyle, paddingTop: '6px' }}>
                        {card.Technology.scienceCost > 0 &&
                            <div className="tta-card-cost science"><b>{card.Technology.scienceCost}</b><Icon images={images} name="Science"/></div>}
                        {card.Technology.workerCost != null &&
                            <div className="tta-card-cost resources"><b>{card.Technology.workerCost}</b><Icon images={images} name="Resource"/></div>}
                        {UnitSymbols[card.Technology.type] &&
                            <div className="tta-special-tile" style={{ margin: '10px auto 0', width: '48px', height: '40px' }}>
                                <Icon images={images} name={UnitSymbols[card.Technology.type]}/>
                            </div>}
                    </div>)
        }
    }
    // A territory: what colonizing it gives once (what it gives while held is along the bottom)
    const payout = card.type === MilitaryCardTypes.Territory ? TerritoryEffects[card.name]?.payout?.(card) : null;
    if (payout)
    {
        contents = <div style={cardContentStyle}><IconText text={payout}/></div>;
    }
    // A tactic: the units it groups into an army, and what that army is worth
    if (card.type === MilitaryCardTypes.Tactic && card.tactic)
    {
        const units = card.tactic.GetUnits();
        // (the value, and in brackets what an army of out-of-date units is worth: "5 (3)")
        const [value, obsolete] = card.tactic.GetValueText().split('(');
        contents = (<div style={{ ...cardContentStyle, paddingTop: '26px' }} className="tta-card-tactic">
            <div className="tta-card-icons">{['Infantry', 'Cavalry', 'Artillery'].map((kind, index) =>
                Repeat(units[index], <span className="tta-unit-badge"><Icon images={images} name={kind}/></span>, kind))}</div>
            <div className="tta-tactic-value">{value.trim()}{obsolete && <span> ({obsolete}</span>}
                {card.tacticBonus > 0 && <span className="tta-tactic-plus"> +{card.tacticBonus}</span>}</div>
            {/* Sun Tzu under this card: his portrait, as Homer's on a wonder he added to */}
            {card.tacticBonusBy && <div className="tta-tactic-by" title={`${card.tacticBonusBy.source}: +${card.tacticBonus} per army on this card`}>
                {images?.[card.tacticBonusBy.image] ? <img src={images[card.tacticBonusBy.image].src} alt={card.tacticBonusBy.source}/> : card.tacticBonusBy.source}</div>}
        </div>);
    }
    return contents;
}

// An image by name, and copies of something (keyed)
const Icon = ({images, name}) => images?.[name] ? <img src={images[name].src} alt={name} className="tta-card-icon"/> : null;
const Repeat = (count, element, key = '') => Array.from({ length: count ?? 0 }, (_, index) => <span key={`${key}${index}`}>{element}</span>);
// A military unit's symbol
const UnitSymbols = { [TechnologyTypes.Military.Infantry]: 'Infantry', [TechnologyTypes.Military.Cavalry]: 'Cavalry', [TechnologyTypes.Military.Artillery]: 'Artillery', [TechnologyTypes.Military.AirForces]: 'AirForces' };
const SpecialSymbols = { [TechnologyTypes.Special.Civil]: 'Civil', [TechnologyTypes.Special.Construction]: 'Construction', [TechnologyTypes.Special.Colonization]: 'Exploration', [TechnologyTypes.Special.Military]: 'Military' };

// Order of the printed bonuses along the bottom of a leader or wonder, and how each is drawn: 'repeat' draws one icon
// per point (actions, happy faces); otherwise the amount and one icon
const BonusIcons = [
    { stat: 'civilActions', image: 'CivilAction', label: 'civil action', repeat: true },
    { stat: 'militaryActions', image: 'MilitaryAction', label: 'military action', repeat: true },
    { stat: 'culture', image: 'Culture', label: 'culture' },
    { stat: 'science', image: 'Science', label: 'science' },
    { stat: 'food', image: 'Food', label: 'food' },
    { stat: 'resources', image: 'Resource', label: 'resources' },
    { stat: 'strength', image: 'Strength', label: 'strength' },
    { stat: 'colonization', image: 'ExplorationResource', label: 'colonization', signed: true },
    { stat: 'happyFaces', image: 'Happiness', label: 'happy faces', repeat: true },
    { stat: 'blueTokens', image: 'BlueCube', label: 'blue tokens', repeat: true, signed: true },
    { stat: 'yellowTokens', image: 'YellowCube', label: 'yellow tokens', repeat: true, signed: true },
];

// A leader's or wonder's permanent bonuses as icons, e.g. "1 [culture] [happy][happy]"; a stat that depends on the
// board shows "?". Hand limits have no icon and are left to the card text.
export const CardBonuses = ({card, images}) =>
{
    // A crumbled wonder (Ravages of Time) has lost its bonuses and produces 2 culture
    const bonusIcons = card.crumbled ? BonusIcons.filter(icon => icon.stat === 'culture') : BonusIcons;
    const shown = card.crumbled ? { ...card, bonuses: { culture: 2 }, varies: [] } : card;
    const groups = bonusIcons.flatMap(({stat, image, label, repeat, signed}) => {
        const card = shown;
        const value = card.bonuses?.[stat];
        if (value === undefined)
        {
            return card.varies?.includes(stat)
                ? [<span key={stat} className="tta-amount" title={`Varies: ${label}`}>?<img src={images[image]?.src} alt={label}/></span>]
                : [];
        }
        // Tokens lost (e.g. a blue token less) are a number with the icon, not an icon each
        if (repeat && !(value < 0 && stat !== 'happyFaces'))
        {
            // An unhappy face for a negative happy face
            const src = value < 0 && stat === 'happyFaces' ? UnhappyIcon : images[image]?.src;
            return [<span key={stat} className="tta-amount" title={`${value} ${label}`}>
                {Array.from({ length: Math.abs(value) }).map((_, index) => <img key={index} src={src} alt={label}/>)}
            </span>];
        }
        const text = `${signed && value > 0 ? '+' : ''}${value}`;
        return [<span key={stat} className="tta-amount" title={`${text} ${label}`}>{text}<img src={images[image]?.src} alt={label}/></span>];
    });
    // Happy faces other cards added to this wonder (Homer), along the bottom with the wonder's own bonuses
    (card.addedHappyFaces ?? []).forEach((added, index) => groups.push(
        <span key={`added ${index}`} className="tta-amount" title={`1 happy face, added by ${added.source}`}>
            <img src={images.Happiness?.src} alt="happy face"/>
        </span>));
    return <div className="tta-card-bonuses">{groups}</div>;
}

// What goes along the bottom of a card: a leader's or wonder's bonuses, a technology's effect, or its own text
export const CardFooterContent = ({card, images}) => {
    // An aggression or war: the military actions it costs to play
    const playCost = card.aggression?.cost ?? card.war?.cost;
    if (playCost)
    {
        return <div className="tta-card-icons">{Repeat(playCost, <Icon images={images} name="MilitaryAction"/>)}</div>;
    }
    if (card.bonuses || card.varies || card.addedHappyFaces?.length)
    {
        return <CardBonuses card={card} images={images}/>;
    }
    // A territory: what it gives while held as a colony
    const territory = card.type === 'Territory' ? TerritoryEffects[card.name]?.bonuses(card) : null;
    if (territory)
    {
        return <CardBonuses card={{ bonuses: territory }} images={images}/>;
    }
    return <TechnologyBenefit card={card} images={images}/>;
};

// A technology's effect along the bottom, as a wonder's bonuses are: what each worker on it gives (farms, mines,
// buildings, units), or what owning it gives (special technologies, governments). Its own text otherwise.
// (Per worker or once owned is said on hover)
const TechnologyBenefit = ({card, images}) =>
{
    const full = CardBenefit(card);
    // (A government's actions are in its body: only anything more goes along the bottom)
    const benefit = full && card.Technology?.type === TechnologyTypes.Government
        ? { ...full, bonuses: Object.fromEntries(Object.entries(full.bonuses).filter(([stat]) => stat !== 'civilActions' && stat !== 'militaryActions')) }
        : full;
    if (!benefit || Object.keys(benefit.bonuses).length === 0)
    {
        return card.benefitText ?? null;
    }
    return (
        <div className="tta-card-benefit" title={benefit.perWorker ? 'What each worker on it gives' : 'What owning it gives'}>
            <CardBonuses card={{ bonuses: benefit.bonuses }} images={images}/>
        </div>
    );
}

export default CardOverlay;